/**
 * Histórico completo de execuções de um agendamento — visual design
 * system. Painel lateral custom (drawer) porque o DS só fornece Modal
 * centralizado; todo o resto (Badge, IconButton, Spinner, EmptyState,
 * Alert, Card, Link) vem do DS.
 *
 * Cada execução é expansível mostrando: tool, duração, status, canais,
 * link SPA pra ChatSession gerada, erros e a mensagem exata enviada
 * por canal (HTML renderizado para e-mail, texto plano para WhatsApp).
 */

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'

import { Badge } from '@/design-system/atoms/Badge'
import { Icon, type IconName } from '@/design-system/atoms/Icon'
import { IconButton } from '@/design-system/atoms/IconButton'
import { Spinner } from '@/design-system/atoms/Spinner'
import { Alert } from '@/design-system/molecules/Alert'
import { EmptyState } from '@/design-system/molecules/EmptyState'
import { cn } from '@/lib/utils'

import schedulerService from '../../services/schedulerService'
import type {
  DeliveryChannel,
  ScheduledTaskExecution,
  ScheduledTaskStatus,
} from '../../types/scheduler'

const TOOL_LABELS: Record<string, string> = {
  list_open_os: 'OS em aberto',
  list_open_hd: 'HDs em aberto',
  list_open_helpdesks: 'HDs em aberto',
  list_open_service_orders: 'OS em aberto',
  cross_reference_os_hd: 'Cruzamento OS × HD',
}

const CHANNEL_LABELS: Record<DeliveryChannel, string> = {
  email: 'E-mail',
  whatsapp: 'WhatsApp',
}

const CHANNEL_ICONS: Record<DeliveryChannel, IconName> = {
  email: 'mail',
  whatsapp: 'chat',
}

const STATUS_META: Record<
  ScheduledTaskStatus,
  { label: string; variant: 'info' | 'success' | 'error' | 'warning' }
> = {
  running: { label: 'Em execução', variant: 'info' },
  success: { label: 'Sucesso', variant: 'success' },
  failed: { label: 'Falhou', variant: 'error' },
  partial: { label: 'Parcial', variant: 'warning' },
}

function formatDateTime(value: string | null): string {
  if (!value) return '—'
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'medium',
    }).format(new Date(value))
  } catch {
    return value
  }
}

function durationHuman(start: string, end: string | null): string {
  if (!end) return '—'
  const diff = new Date(end).getTime() - new Date(start).getTime()
  if (Number.isNaN(diff) || diff < 0) return '—'
  const seconds = Math.round(diff / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}min ${seconds % 60}s`
}

interface Props {
  taskId: string
  taskName?: string
  onClose: () => void
}

export default function ExecutionHistoryPanel({
  taskId,
  taskName,
  onClose,
}: Props) {
  const [executions, setExecutions] = useState<ScheduledTaskExecution[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const list = await schedulerService.listExecutions(taskId)
      setExecutions(list)
    } finally {
      setLoading(false)
    }
  }, [taskId])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <aside
        className="flex h-full w-full flex-col overflow-hidden border-l border-gray-200 bg-white shadow-2xl sm:max-w-2xl dark:border-gray-800 dark:bg-gray-900"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-label={taskName ? `Histórico: ${taskName}` : 'Histórico'}
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-3 border-b border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-800 dark:bg-gray-900/60">
          <div className="min-w-0 flex-1">
            <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
              <Icon name="list" size={20} className="text-primary-600" />
              Histórico de execuções
            </h3>
            {taskName && (
              <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                {taskName}
              </p>
            )}
          </div>
          <IconButton
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Fechar histórico"
          >
            <Icon name="x" size={20} />
          </IconButton>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Spinner size="md" className="text-primary-600" />
            </div>
          )}
          {!loading && executions.length === 0 && (
            <EmptyState
              iconName="inbox"
              size="sm"
              title="Nenhuma execução registrada"
              description='Execute pelo botão "Executar agora" ou aguarde o próximo horário agendado.'
            />
          )}

          {!loading && executions.length > 0 && (
            <ul className="space-y-3">
              {executions.map(exec => (
                <ExecutionCard
                  key={exec.id}
                  exec={exec}
                  expanded={expanded === exec.id}
                  onToggleExpand={() =>
                    setExpanded(prev => (prev === exec.id ? null : exec.id))
                  }
                  onCloseHistory={onClose}
                />
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  )
}

/* -------------------------------------------------------------------------- */

interface CardProps {
  exec: ScheduledTaskExecution
  expanded: boolean
  onToggleExpand: () => void
  onCloseHistory: () => void
}

function ExecutionCard({
  exec,
  expanded,
  onToggleExpand,
  onCloseHistory,
}: CardProps) {
  const meta = STATUS_META[exec.status] ?? STATUS_META.running
  const toolLabel = TOOL_LABELS[exec.tool_name] ?? exec.tool_name

  return (
    <li className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900/50">
      <button
        type="button"
        onClick={onToggleExpand}
        aria-expanded={expanded}
        className="w-full p-3 text-left transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:hover:bg-gray-800/50"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {formatDateTime(exec.started_at)}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium">{toolLabel}</span>
              <span className="text-gray-300 dark:text-gray-700">·</span>
              <span>{durationHuman(exec.started_at, exec.finished_at)}</span>
              {exec.delivered_channels.length > 0 && (
                <>
                  <span className="text-gray-300 dark:text-gray-700">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    {exec.delivered_channels.map(c => (
                      <Icon
                        key={c}
                        name={CHANNEL_ICONS[c]}
                        size={14}
                        aria-label={CHANNEL_LABELS[c]}
                      />
                    ))}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Badge variant={meta.variant} size="sm" shape="pill">
              {meta.label}
            </Badge>
            <Icon
              name={expanded ? 'chevron-up' : 'chevron-down'}
              size={16}
              className="text-gray-400"
            />
          </div>
        </div>
      </button>

      {expanded && (
        <div className="space-y-3 border-t border-gray-100 px-3 pb-3 pt-3 dark:border-gray-800">
          {exec.chat_session && (
            <Link
              to={`/agents/${exec.chat_session.agent_type}/chat?sessionId=${exec.chat_session.id}`}
              onClick={onCloseHistory}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 transition-colors hover:underline dark:text-primary-400"
              title={exec.chat_session.title}
            >
              <Icon name="message-square-text" size={14} />
              Abrir chat gerado
              <Icon name="arrow-right" size={12} />
            </Link>
          )}

          {exec.error_message && (
            <Alert variant="error" title="Erro de execução">
              <p className="whitespace-pre-wrap break-words">
                {exec.error_message}
              </p>
            </Alert>
          )}

          {Object.entries(exec.delivery_errors).map(([channel, msg]) => (
            <Alert
              key={channel}
              variant="warning"
              title={`Falha no canal ${CHANNEL_LABELS[channel as DeliveryChannel] ?? channel}`}
            >
              {msg}
            </Alert>
          ))}

          {exec.delivered_messages?.email && (
            <DeliveredMessageSection
              channel="email"
              title="Mensagem enviada por e-mail"
              recipients={exec.delivered_messages.email.recipients}
              subject={exec.delivered_messages.email.subject}
              html={exec.delivered_messages.email.html}
            />
          )}

          {exec.delivered_messages?.whatsapp && (
            <DeliveredMessageSection
              channel="whatsapp"
              title="Mensagem enviada por WhatsApp"
              recipients={exec.delivered_messages.whatsapp.recipients}
              text={exec.delivered_messages.whatsapp.body}
            />
          )}
        </div>
      )}
    </li>
  )
}

/* -------------------------------------------------------------------------- */

interface DeliveredMessageSectionProps {
  channel: DeliveryChannel
  title: string
  recipients: string[]
  subject?: string
  html?: string
  text?: string
}

function DeliveredMessageSection({
  channel,
  title,
  recipients,
  subject,
  html,
  text,
}: DeliveredMessageSectionProps) {
  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800">
      <header className="border-b border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-800 dark:bg-gray-800/50">
        <p className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-200">
          <Icon name={CHANNEL_ICONS[channel]} size={14} />
          {title}
        </p>
        <div className="mt-1 space-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
          <p>
            <span className="font-medium">Para:</span>{' '}
            {recipients.length > 0 ? recipients.join(', ') : '—'}
          </p>
          {subject && (
            <p>
              <span className="font-medium">Assunto:</span> {subject}
            </p>
          )}
        </div>
      </header>
      <div className={cn('bg-white dark:bg-gray-900', html ? '' : '')}>
        {html ? (
          <div
            className="max-h-96 overflow-auto bg-white"
            /* HTML controlado, gerado pelo formatter do backend com
             * html.escape em todos os valores (ver apps/scheduler/delivery/
             * formatter.py::_escape). */
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : text ? (
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words p-3 font-mono text-xs text-gray-700 dark:text-gray-300">
            {text}
          </pre>
        ) : (
          <p className="p-3 text-xs italic text-gray-400">
            Nenhum conteúdo disponível.
          </p>
        )}
      </div>
    </section>
  )
}
